/**
 * Rotulos de Configuracoes em portugues, ingles e espanhol.
 *
 * Os getters acompanham o idioma do shell sem alterar o dicionario do nucleo.
 */
import { useIdioma } from '../../i18n/index.tsx';

const pt = {
  titulo: 'Configurações',
  empresa: 'Empresa',
  aparencia: 'Aparência',
  sequencia: 'Sequências',
  campo: 'Campos personalizados',
  visao: 'Visões salvas',
  modelo: 'Modelos',
  meta_vendas: 'Meta de vendas',
  mes: 'Mês (AAAA-MM)',
  valor: 'Valor da meta (R$)',
  funcao: 'Funções',
  novo: 'Novo registro',
  salvar: 'Salvar',
  cancelar: 'Cancelar',
  editar: 'Editar',
  excluir: 'Arquivar',
  confirmar: 'Confirmar arquivamento',
  vazio: 'Nenhum registro cadastrado',
  vazioTexto: 'Use o formulário para criar o primeiro registro.',
  carregando: 'Carregando configurações',
  erro: 'Não foi possível concluir. Confira os campos e tente novamente.',
  sucesso: 'Alteração salva.',
  historico: 'Histórico de alterações',
  antes: 'Antes',
  depois: 'Depois',
  repetir: 'Tentar novamente',
  naoInstalado: 'Módulo não instalado',
  externos: 'Catálogos de outros módulos',
  pipeline: 'Etapas do pipeline',
  financeiro: 'Categorias financeiras',
  abrir: 'Abrir módulo',
  razao_social: 'Razão social',
  nome_fantasia: 'Nome fantasia',
  documento: 'CNPJ (14 dígitos)',
  logo_ref: 'Referência do logo (HTTPS ou anexo local)',
  endereco: 'Endereço (JSON)',
  extras: 'Campos extras (JSON)',
  acento: 'Acento de marca (matiz)',
  tema: 'Tema padrão',
  idioma: 'Idioma padrão',
  codigo: 'Documento numerado',
  empresa_id: 'Empresa',
  prefixo: 'Prefixo',
  padding: 'Quantidade de dígitos',
  reinicia_por_ano: 'Reiniciar por ano',
  modulo: 'Módulo',
  entidade: 'Cadastro',
  chave: 'Chave do campo',
  rotulo: 'Rótulo',
  tipo: 'Tipo',
  opcoes: 'Opções (uma por linha)',
  obrigatorio: 'Obrigatório',
  ordem: 'Ordem',
  tela: 'Tela',
  nome: 'Nome',
  filtros: 'Filtros (JSON)',
  colunas: 'Colunas (uma por linha)',
  ordenacao: 'Ordenação (JSON)',
  padrao: 'Visão padrão',
  assunto: 'Assunto',
  corpo: 'Conteúdo',
  descricao: 'Descrição',
  escuro: 'Escuro',
  claro: 'Claro',
  pt: 'Português',
  en: 'Inglês',
  es: 'Espanhol',
  pedido: 'Pedido',
  fatura: 'Fatura',
  contrato: 'Contrato',
  texto: 'Texto',
  numero: 'Número',
  data: 'Data',
  lista: 'Lista',
  booleano: 'Sim ou não',
  mensagem: 'Mensagem',
  documentoModelo: 'Documento',
  nucleo: 'Núcleo',
  cadastros: 'Cadastros',
  empresas: 'Empresas',
  parceiros: 'Parceiros',
  projetos: 'Projetos',
  geral: 'Todas as empresas',
  proximo: 'Próximo número',
  modeloAjuda: 'Variáveis permitidas: {{nome}}, {{empresa}}, {{projeto}}, {{data}}.',
  jsonErro: 'Informe um JSON válido.',
  semHistorico: 'Nenhuma alteração registrada.',
  sim: 'Sim',
  nao: 'Não',
  preview: 'Prévia da marca',
  privada: 'Visão pessoal do usuário conectado',
  identidade: 'Tipo, chave, cadastro e opções ficam fixos depois da criação.',
  valorInvalido: 'Valor inválido',
  duplicado: 'Já existe um registro com essa identificação.'
};

export type ChaveConfiguracoes = keyof typeof pt;
const en: Record<ChaveConfiguracoes, string> = {
  titulo: 'Settings',
  empresa: 'Company',
  aparencia: 'Appearance',
  sequencia: 'Sequences',
  campo: 'Custom fields',
  visao: 'Saved views',
  modelo: 'Templates',
  meta_vendas: 'Sales target',
  mes: 'Month (YYYY-MM)',
  valor: 'Target amount (R$)',
  funcao: 'Roles',
  novo: 'New record',
  salvar: 'Save',
  cancelar: 'Cancel',
  editar: 'Edit',
  excluir: 'Archive',
  confirmar: 'Confirm archive',
  vazio: 'No records yet',
  vazioTexto: 'Use the form to create the first record.',
  carregando: 'Loading settings',
  erro: 'Could not complete. Check the fields and try again.',
  sucesso: 'Changes saved.',
  historico: 'Change history',
  antes: 'Before',
  depois: 'After',
  repetir: 'Try again',
  naoInstalado: 'Module not installed',
  externos: 'Other module catalogs',
  pipeline: 'Pipeline stages',
  financeiro: 'Financial categories',
  abrir: 'Open module',
  razao_social: 'Legal name',
  nome_fantasia: 'Trading name',
  documento: 'Company tax ID (14 digits)',
  logo_ref: 'Logo reference (HTTPS or local attachment)',
  endereco: 'Address (JSON)',
  extras: 'Extra fields (JSON)',
  acento: 'Brand accent (hue)',
  tema: 'Default theme',
  idioma: 'Default language',
  codigo: 'Numbered document',
  empresa_id: 'Company',
  prefixo: 'Prefix',
  padding: 'Number of digits',
  reinicia_por_ano: 'Reset each year',
  modulo: 'Module',
  entidade: 'Entity',
  chave: 'Field key',
  rotulo: 'Label',
  tipo: 'Type',
  opcoes: 'Options (one per line)',
  obrigatorio: 'Required',
  ordem: 'Order',
  tela: 'Screen',
  nome: 'Name',
  filtros: 'Filters (JSON)',
  colunas: 'Columns (one per line)',
  ordenacao: 'Sorting (JSON)',
  padrao: 'Default view',
  assunto: 'Subject',
  corpo: 'Content',
  descricao: 'Description',
  escuro: 'Dark',
  claro: 'Light',
  pt: 'Portuguese',
  en: 'English',
  es: 'Spanish',
  pedido: 'Order',
  fatura: 'Invoice',
  contrato: 'Contract',
  texto: 'Text',
  numero: 'Number',
  data: 'Date',
  lista: 'List',
  booleano: 'Yes or no',
  mensagem: 'Message',
  documentoModelo: 'Document',
  nucleo: 'Core',
  cadastros: 'Records',
  empresas: 'Companies',
  parceiros: 'Partners',
  projetos: 'Projects',
  geral: 'All companies',
  proximo: 'Next number',
  modeloAjuda: 'Supported variables: {{nome}}, {{empresa}}, {{projeto}}, {{data}}.',
  jsonErro: 'Enter valid JSON.',
  semHistorico: 'No changes recorded.',
  sim: 'Yes',
  nao: 'No',
  preview: 'Brand preview',
  privada: 'Personal view for the signed in user',
  identidade: 'Type, key, entity and options stay fixed after creation.',
  valorInvalido: 'Invalid value',
  duplicado: 'A record with this identification already exists.'
};
const es: Record<ChaveConfiguracoes, string> = {
  titulo: 'Configuracion',
  empresa: 'Empresa',
  aparencia: 'Apariencia',
  sequencia: 'Secuencias',
  campo: 'Campos personalizados',
  visao: 'Vistas guardadas',
  modelo: 'Plantillas',
  meta_vendas: 'Meta de ventas',
  mes: 'Mes (AAAA-MM)',
  valor: 'Valor de la meta (R$)',
  funcao: 'Funciones',
  novo: 'Nuevo registro',
  salvar: 'Guardar',
  cancelar: 'Cancelar',
  editar: 'Editar',
  excluir: 'Archivar',
  confirmar: 'Confirmar archivo',
  vazio: 'No hay registros',
  vazioTexto: 'Usa el formulario para crear el primer registro.',
  carregando: 'Cargando configuración',
  erro: 'No se pudo completar. Revisa los campos e intenta de nuevo.',
  sucesso: 'Cambios guardados.',
  historico: 'Historial de cambios',
  antes: 'Antes',
  depois: 'Despues',
  repetir: 'Intentar de nuevo',
  naoInstalado: 'Módulo no instalado',
  externos: 'Catálogos de otros módulos',
  pipeline: 'Etapas del pipeline',
  financeiro: 'Categorias financieras',
  abrir: 'Abrir módulo',
  razao_social: 'Razón social',
  nome_fantasia: 'Nombre comercial',
  documento: 'Identificación fiscal (14 dígitos)',
  logo_ref: 'Referencia del logo (HTTPS o adjunto local)',
  endereco: 'Dirección (JSON)',
  extras: 'Campos extra (JSON)',
  acento: 'Acento de marca (matiz)',
  tema: 'Tema predeterminado',
  idioma: 'Idioma predeterminado',
  codigo: 'Documento numerado',
  empresa_id: 'Empresa',
  prefixo: 'Prefijo',
  padding: 'Cantidad de dígitos',
  reinicia_por_ano: 'Reiniciar cada ano',
  modulo: 'Módulo',
  entidade: 'Registro',
  chave: 'Clave del campo',
  rotulo: 'Etiqueta',
  tipo: 'Tipo',
  opcoes: 'Opciones (una por línea)',
  obrigatorio: 'Obligatorio',
  ordem: 'Orden',
  tela: 'Pantalla',
  nome: 'Nombre',
  filtros: 'Filtros (JSON)',
  colunas: 'Columnas (una por línea)',
  ordenacao: 'Ordenación (JSON)',
  padrao: 'Vista predeterminada',
  assunto: 'Asunto',
  corpo: 'Contenido',
  descricao: 'Descripción',
  escuro: 'Oscuro',
  claro: 'Claro',
  pt: 'Portugués',
  en: 'Inglés',
  es: 'Español',
  pedido: 'Pedido',
  fatura: 'Factura',
  contrato: 'Contrato',
  texto: 'Texto',
  numero: 'Número',
  data: 'Fecha',
  lista: 'Lista',
  booleano: 'Si o no',
  mensagem: 'Mensaje',
  documentoModelo: 'Documento',
  nucleo: 'Núcleo',
  cadastros: 'Registros',
  empresas: 'Empresas',
  parceiros: 'Socios',
  projetos: 'Proyectos',
  geral: 'Todas las empresas',
  proximo: 'Próximo número',
  modeloAjuda: 'Variables permitidas: {{nome}}, {{empresa}}, {{projeto}}, {{data}}.',
  jsonErro: 'Introduce un JSON válido.',
  semHistorico: 'No hay cambios registrados.',
  sim: 'Si',
  nao: 'No',
  preview: 'Vista previa de marca',
  privada: 'Vista personal del usuario conectado',
  identidade: 'Tipo, clave, registro y opciones quedan fijos después de crear.',
  valorInvalido: 'Valor inválido',
  duplicado: 'Ya existe un registro con esta identificación.'
};
export const dicionarios = {
  pt,
  en,
  es
};

export function rotuloAtual(chave: ChaveConfiguracoes): string {
  let idioma = typeof document === 'undefined' ? 'pt' : document.documentElement.lang;
  try {
    idioma = localStorage.getItem('erp_idioma') ?? idioma;
  } catch {
  }
  return dicionarios[idioma === 'en' || idioma === 'es' ? idioma : 'pt'][chave];
}

export function useTraducao() {
  const { idioma } = useIdioma();
  return (chave: ChaveConfiguracoes) => dicionarios[idioma][chave];
}
